import React, { useMemo, useState, Component } from 'react';
import { Database, Layers, Component as ComponentIcon, Tag } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { type Question, ALL_QUESTIONS_CORE, QUESTIONS_BY_FAMILY, QUESTION_TYPE_REGISTRY } from '../../../content';
import { VISUAL_REGISTRY } from '../../../visuals';
import { OverviewTab } from '../content/questionBank/OverviewTab';
import { TypesTab } from '../content/questionBank/TypesTab';
import { VisualsTab } from '../content/questionBank/VisualsTab';
import { FamiliesTab } from '../content/questionBank/FamiliesTab';
import { QuestionBrowser } from '../content/questionBank/QuestionBrowser';
import { ADMIN_MODULE_META } from '../shared/moduleMetadata';
// Subject metadata — derived from canonical source
const SUBJECT_META = ADMIN_MODULE_META;
type TabId = 'overview' | 'types' | 'visuals' | 'families';
const TABS = [{
  id: 'overview' as const,
  label: 'Vakken',
  icon: <Database className="w-4 h-4" data-id="element-4956" />
}, {
  id: 'types' as const,
  label: 'Typen',
  icon: <Layers className="w-4 h-4" data-id="element-4957" />
}, {
  id: 'visuals' as const,
  label: 'Visuals',
  icon: <ComponentIcon className="w-4 h-4" data-id="element-4958" />
}, {
  id: 'families' as const,
  label: 'Families',
  icon: <Tag className="w-4 h-4" data-id="element-4959" />
}];
// Browser mode state
type BrowserMode = {
  type: 'none';
} | {
  type: 'subject';
  code: string;
} | {
  type: 'questionType';
  questionType: string;
} | {
  type: 'visual';
  visualType: string;
} | {
  type: 'family';
  familyId: string;
};
interface QuestionBankPageProps {
  questions?: Question[];
}
export function QuestionBankPage({
  questions = []
}: QuestionBankPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [browserMode, setBrowserMode] = useState<BrowserMode>({
    type: 'none'
  });
  // Use provided questions or fall back to ALL_QUESTIONS_CORE
  const allQuestions = useMemo(() => {
    return questions.length > 0 ? questions : ALL_QUESTIONS_CORE;
  }, [questions]);
  // Compute subjects data
  const subjects = useMemo(() => {
    const bySubject: Record<string, {
      count: number;
      families: Set<string>;
      tiers: number[];
    }> = {};
    allQuestions.forEach(q => {
      const code = q.familyId?.split('-')[0] || 'UNK';
      if (!bySubject[code]) {
        bySubject[code] = {
          count: 0,
          families: new Set(),
          tiers: []
        };
      }
      bySubject[code].count++;
      bySubject[code].families.add(q.familyId);
      if (q.tier) bySubject[code].tiers.push(q.tier);
    });
    return Object.entries(bySubject).map(([code, data]) => {
      const meta = SUBJECT_META[code] || {
        name: code,
        color: 'bg-gray-500'
      };
      const avgTier = data.tiers.length > 0 ? Math.round(data.tiers.reduce((a, b) => a + b, 0) / data.tiers.length) : 0;
      return {
        code,
        name: meta.name,
        color: meta.color,
        questionCount: data.count,
        familyCount: data.families.size,
        avgTier
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute question types data
  const types = useMemo(() => {
    const byType: Record<string, {
      count: number;
      families: Set<string>;
    }> = {};
    allQuestions.forEach(q => {
      const t = q.questionType;
      if (!byType[t]) {
        byType[t] = {
          count: 0,
          families: new Set()
        };
      }
      byType[t].count++;
      byType[t].families.add(q.familyId);
    });
    return Object.entries(byType).map(([type, data]) => {
      const isRegistered = type in QUESTION_TYPE_REGISTRY;
      return {
        type,
        questionCount: data.count,
        familyCount: data.families.size,
        isRegistered
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute visuals data
  const visuals = useMemo(() => {
    const byVisual: Record<string, {
      count: number;
      families: Set<string>;
    }> = {};
    allQuestions.forEach(q => {
      const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
      aids.forEach(aid => {
        if (!aid.type) return;
        if (!byVisual[aid.type]) {
          byVisual[aid.type] = {
            count: 0,
            families: new Set()
          };
        }
        byVisual[aid.type].count++;
        byVisual[aid.type].families.add(q.familyId);
      });
    });
    return Object.entries(byVisual).map(([type, data]) => {
      const isRegistered = type in VISUAL_REGISTRY;
      return {
        type,
        questionCount: data.count,
        familyCount: data.families.size,
        isRegistered
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute families data
  const families = useMemo(() => {
    return Array.from(QUESTIONS_BY_FAMILY.entries()).map(([id, qs]) => {
      const subject = id.split('-')[0] || 'UNK';
      const tiers = qs.map(q => q.tier).filter(Boolean) as number[];
      const avgTier = tiers.length > 0 ? Math.round(tiers.reduce((a, b) => a + b, 0) / tiers.length) : 0;
      return {
        id,
        subject,
        questionCount: qs.length,
        avgTier
      };
    }).sort((a, b) => a.id.localeCompare(b.id));
  }, []);
  // Derive browser props from mode
  const browserProps = useMemo(() => {
    if (browserMode.type === 'none') return null;
    switch (browserMode.type) {
      case 'subject':
        {
          const meta = SUBJECT_META[browserMode.code] || {
            name: browserMode.code,
            color: 'bg-gray-500'
          };
          const filtered = allQuestions.filter(q => q.familyId?.split('-')[0] === browserMode.code);
          const familyCount = new Set(filtered.map(q => q.familyId)).size;
          return {
            title: meta.name,
            subtitle: `${familyCount} families · ${filtered.length} vragen`,
            questions: filtered,
            accentColor: 'blue' as const
          };
        }
      case 'questionType':
        {
          const filtered = allQuestions.filter(q => q.questionType === browserMode.questionType);
          return {
            title: browserMode.questionType,
            subtitle: `${filtered.length} vragen met dit type`,
            questions: filtered,
            accentColor: 'purple' as const
          };
        }
      case 'visual':
        {
          const filtered = allQuestions.filter(q => {
            const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
            return aids.some(a => a.type === browserMode.visualType);
          });
          return {
            title: browserMode.visualType,
            subtitle: `${filtered.length} vragen met deze visual`,
            questions: filtered,
            accentColor: 'emerald' as const
          };
        }
      case 'family':
        {
          const filtered = allQuestions.filter(q => q.familyId === browserMode.familyId);
          return {
            title: browserMode.familyId,
            subtitle: `${filtered.length} vragen`,
            questions: filtered,
            accentColor: 'amber' as const,
            groupBy: 'none' as const
          };
        }
    }
  }, [browserMode, allQuestions]);
  // If browser is active, render it instead of tabs
  if (browserMode.type !== 'none' && browserProps) {
    return <div className="flex flex-col h-full min-h-0 p-4 sm:p-6 pt-2 sm:pt-4 overflow-hidden" data-id="element-4960">
        <QuestionBrowser title={browserProps.title} subtitle={browserProps.subtitle} questions={browserProps.questions} onBack={() => setBrowserMode({
        type: 'none'
      })} accentColor={browserProps.accentColor} groupBy={'groupBy' in browserProps ? browserProps.groupBy : 'family'} data-id="element-4961" />
      </div>;
  }
  return <PageLayout title="Vragenbank" subtitle="Verken alle vragen, types en visuele componenten" breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Vragenbank'
  }]} data-id="element-4962">
      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-4963" />

      <div className="mt-4" data-id="element-4964">
        {activeTab === 'overview' && <OverviewTab subjects={subjects} totalQuestions={allQuestions.length} totalFamilies={families.length} typeCount={types.length} visualCount={visuals.length} onBrowseSubject={code => setBrowserMode({
        type: 'subject',
        code
      })} data-id="element-4965" />}
        {activeTab === 'types' && <TypesTab questions={allQuestions} onSelectType={type => setBrowserMode({
        type: 'questionType',
        questionType: type
      })} data-id="element-4966" />}
        {activeTab === 'visuals' && <VisualsTab questions={allQuestions} onSelectVisual={type => setBrowserMode({
        type: 'visual',
        visualType: type
      })} data-id="element-4967" />}
        {activeTab === 'families' && <FamiliesTab questions={allQuestions} onSelectFamily={id => setBrowserMode({
        type: 'family',
        familyId: id
      })} data-id="element-4968" />}
      </div>
    </PageLayout>;
}